import { Controller } from "@hotwired/stimulus";

// Suggests help articles while someone types a support request, so an answer
// that already exists can be found before the ticket is filed.
//
// Deliberately no LLM: the published help corpus is small enough that the
// existing weighted pg_search endpoint matches at least as well, with no added
// latency, no cost per keystroke, and no request text leaving the app.
export default class extends Controller<HTMLElement> {
  static targets = ["panel", "results"];
  static values = {
    searchUrl: String,
    navigation: Object,
    debounce: { type: Number, default: 500 },
    minLength: { type: Number, default: 3 },
    limit: { type: Number, default: 3 },
  };

  declare readonly panelTarget: HTMLElement;
  declare readonly resultsTarget: HTMLElement;
  declare readonly searchUrlValue: string;
  declare readonly navigationValue: Record<string, string>;
  declare readonly debounceValue: number;
  declare readonly minLengthValue: number;
  declare readonly limitValue: number;

  private timeout?: number;
  private controller?: AbortController;

  disconnect(): void {
    if (this.timeout) window.clearTimeout(this.timeout);
    this.controller?.abort();
  }

  search(): void {
    if (this.timeout) window.clearTimeout(this.timeout);
    this.timeout = window.setTimeout(() => this.run(), this.debounceValue);
  }

  private query(): string {
    const fields = this.element.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      "input[type=text], textarea"
    );
    return Array.from(fields)
      .map((field) => field.value)
      .join(" ")
      .trim();
  }

  private async run(): Promise<void> {
    const query = this.query();

    if (query.length < this.minLengthValue) {
      this.hide();
      return;
    }

    this.controller?.abort();
    this.controller = new AbortController();

    try {
      const response = await fetch(`${this.searchUrlValue}?match=any&q=${encodeURIComponent(query)}`, {
        headers: { Accept: "application/json" },
        signal: this.controller.signal,
      });

      if (!response.ok) {
        this.hide();
        return;
      }

      const payload = await response.json();
      this.render(payload.results || []);
    } catch (error) {
      if ((error as Error).name === "AbortError") return;
      // A failed lookup must never get in the way of filing the request.
      this.hide();
    }
  }

  private render(results: Array<{ title: string; excerpt?: string; url: string }>): void {
    if (results.length === 0) {
      this.hide();
      return;
    }

    this.resultsTarget.innerHTML = "";

    results.slice(0, this.limitValue).forEach((result) => {
      const link = document.createElement("a");
      link.href = result.url;
      this.openInPlace(link);
      link.className = "block rounded-md border border-base-300 p-2 hover:bg-base-200";

      const title = document.createElement("div");
      title.className = "text-sm font-medium";
      title.textContent = result.title;
      link.appendChild(title);

      if (result.excerpt) {
        const excerpt = document.createElement("div");
        excerpt.className = "text-xs text-gray-500 line-clamp-2";
        excerpt.textContent = result.excerpt;
        link.appendChild(excerpt);
      }

      this.resultsTarget.appendChild(link);
    });

    this.panelTarget.classList.remove("hidden");
  }

  // Reading a suggestion must never cost someone the request they are part-way
  // through writing. The dynamic modal keeps them on the page, exactly as a
  // contextual help link does — every layout that reaches this form mounts it.
  private openInPlace(link: HTMLAnchorElement): void {
    Object.entries(this.navigationValue).forEach(([key, value]) => {
      link.setAttribute(`data-${key.replaceAll("_", "-")}`, value);
    });
  }

  private hide(): void {
    this.panelTarget.classList.add("hidden");
    this.resultsTarget.innerHTML = "";
  }
}
